<template>
  <div>
    <modal
      :show="show"
      @hidden="$emit('input', false)"
      body-class="spotlight-search-modal-body"
      :hide-footer="true"
      :hide-header="true"
      :load-in-place="true"
      :scrollable="true"
      size="lg"
    >
      <div class="search-wrapper">
        <i class="search-icon"></i>
        <input
          type="search"
          id="spotlight-search"
          v-model="searchText"
          @keyup.enter="viewItem"
          placeholder="Search"
          class="search-field text-extra-large"
          autocomplete="off"
          ref="searchField"
          v-autofocus
        />
      </div>
      <spotlight-search-filters
        :filter-data="filterData"
        :org-id="orgId"
        :sub-org-id="subOrgId"
        @change="onFilterChange"
      />
      <div ref="results">
        <div class="loaders" v-if="searching">
          <skeleton avatar-size="20" line-height="20" class="my-4" />
          <skeleton avatar-size="20" line-height="20" class="my-4" />
          <skeleton avatar-size="20" line-height="20" class="my-4" />
        </div>
        <spotlight-search-results
          v-else-if="(searchText || isFiltersApplied()) && hasSearchResults"
          :search-results="searchResults"
          :sub-org-id="subOrgId"
          :org-id="orgId"
          @item-click="$emit('input', false)"
        />
        <div
          v-else-if="(searchText || isFiltersApplied()) && searchResults.goals && !hasSearchResults"
          class="no-results"
        >
          We couldn't find anything that matched your search.
        </div>
        <recent-pages
          heading="Recently visited pages"
          :org-id="orgId"
          v-else-if="show"
          @item-click="onRecentItemsClick"
        />
      </div>
    </modal>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { intervalWithId, ACTIVE_QUARTER_ID } from '@/interval'
import urlManager from '@/urlManager'
import { camelCaseToSnakeCase } from '@/utils/objectCasingConvertors'
import * as R from 'ramda'
import Modal from '@/components/Modal'
import RecentPages from '@/components/RecentPages'
import SpotlightSearchFilters from '@/components/SpotlightSearchFilters'
import SpotlightSearchResults from '@/components/SpotlightSearchResults'
import Skeleton from '@/components/skeletons/AvatarTitle'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import * as keymapService from '@/services/keymapService/keymapService'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import service from '@/service'
import debounce from '@/utils/debounce'

const defaultFilter = {
  timeline: ACTIVE_QUARTER_ID
}
export default {
  name: 'SpotlightSearch',
  props: ['orgId', 'show'],
  mixins: [ListNavigationMixin],
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    RecentPages,
    SpotlightSearchResults,
    SpotlightSearchFilters,
    Skeleton
  },
  data() {
    return {
      searchText: '',
      filterData: defaultFilter,
      searchResults: {},
      search: debounce(this.performSearch, 500),
      searching: false,
      /**
       * This is used for next, and previous navigation using keyboard in ListNavigationMixin
       * @returns {HTMLDivElement}
       */
      listNode: null
    }
  },
  computed: {
    hasSearchResults() {
      return R.flatten(Object.values(this.searchResults)).length > 0
    },
    subOrgId() {
      return this.$store.state.orgSelect.selectedOrgLikeTeamId
    }
  },
  methods: {
    setListNode() {
      this.listNode = this.$refs.results
    },
    isFiltersApplied() {
      return Object.values(this.filterData).filter(x => !R.isNil(x)).length > 0
    },
    viewItem() {
      const selected = this.getSelected()
      if (
        selected &&
        (selected.classList.contains('page') || selected.classList.contains('search-item'))
      ) {
        const route = selected.dataset.location
        this.$router.push(JSON.parse(route))
        this.$emit('input', false)
      }
    },
    activeKeymapCategory() {
      keymapService.activateCategory('spotlightSearch', {
        'previous-search-item': {
          alwaysExecute: true,
          callback: this.selectPreviousItem
        },
        'next-search-item': {
          alwaysExecute: true,
          callback: this.selectNextItem
        },
        'view-search-item': {
          alwaysExecute: true,
          callback: this.viewItem
        }
      })
    },
    deactivateKeymapCategory() {
      keymapService.deactivateCategory('spotlightSearch')
    },
    getTimelineParams() {
      const params = {}
      if (this.filterData.timeline) {
        const timeline = intervalWithId(this.filterData.timeline)
        params.intervalStart = timeline.start
        params.intervalEnd = timeline.end
        params.intervalSubset = timeline.subset
      }
      return params
    },
    async performSearch() {
      if (this.searchText || this.isFiltersApplied()) {
        try {
          const timelineParams = this.getTimelineParams()
          const filterParams = {
            ...timelineParams,
            ...R.pick(['ownerId', 'teamId', 'resultTypes'], this.filterData)
          }
          this.searching = true
          trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.SEARCH, EVENT_NAME.SPOTLIGHT)
          const response = await service.client.get(urlManager.search(this.orgId), {
            params: {
              text: this.searchText,
              ...camelCaseToSnakeCase(filterParams),
              is_archived: false,
              scope_id: this.subOrgId
            }
          })
          this.searching = false
          this.searchResults = response.data
        } catch (e) {
          const errorMessage = R.pathOr(DEFAULT_ERROR, ['response', 'message'], e)
          EventBus.$emit('ADD_TOAST', { message: errorMessage, type: TOAST_TYPE.ERROR })
          this.searching = false
        }
      } else {
        this.searchResults = {}
      }
    },
    trackEvent(field, fieldValue) {
      if (field === 'resultTypes') {
        let eventName
        switch (fieldValue) {
          case 'goals,krs':
            eventName = EVENT_NAME.GOAL_AND_KR
            break
          case 'comments':
            eventName = EVENT_NAME.COMMENTS
            break
          case 'initiatives':
            eventName = EVENT_NAME.INITIATIVES_TASKS
            break
        }
        if (eventName) {
          trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.SEARCH, eventName)
        }
      }
    },
    async onFilterChange({ field, fieldValue }) {
      this.filterData = R.assoc(field, fieldValue, this.filterData)
      this.performSearch()
      this.trackEvent(field, fieldValue)
    },
    onRecentItemsClick() {
      trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.SEARCH, EVENT_NAME.RECENT)
      this.$emit('input', false)
    }
  },
  watch: {
    show(show) {
      if (show) {
        this.searchText = ''
        this.filterData = defaultFilter
        this.activeKeymapCategory()
        trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.SPOTLIGHT_SEARCH_MODAL)
        this.$nextTick(this.setListNode)
      } else {
        this.deactivateKeymapCategory()
      }
    },
    searchText: 'search',
    searchResults(results) {
      window.north.searchResults = results
    }
  },
  beforeDestroy() {
    this.deactivateKeymapCategory()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/_custom.scss';

.search-wrapper {
  position: sticky;
  margin-bottom: 32px;
  padding-top: 20px;
  top: 0;
  z-index: 1;
  background: $documentColor;
  border-radius: $modal-content-border-radius;
}

.search-field {
  border: none;
  border-bottom: 1px solid $borderPrimary;
  padding: 20px 40px 25px 83px;
  width: 100%;

  &:focus {
    outline: none;
  }
}

.search-icon {
  height: 21px;
  width: 21px;
  position: absolute;
  left: 40px;
  top: 48px;
}

.loaders {
  padding: 0 42px;
}

.no-results {
  padding: 0 40px;
}
</style>
<style lang="scss">
.modal-body.spotlight-search-modal-body {
  padding: 0 0 10px 0;
  min-height: 350px;
}
</style>
